import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TrendingUp, Home, DollarSign, BarChart3, AlertTriangle, ArrowDownLeft, ArrowUpRight, CalendarDays, ListChecks, CreditCard, Eye, FileText, Building2, Target, Pencil, Check, X, CheckCircle2, Circle, MapPin, Filter, Download, Users } from 'lucide-react';
import { MoneyInput } from '@/components/MoneyInput';
import { CommissionSummary } from '@/components/CommissionSummary';
import { BrokerCommissionChart } from '@/components/BrokerCommissionChart';
import { FollowUpRanking } from '@/components/FollowUpRanking';
import { LostReasonsSummary } from '@/components/LostReasonsSummary';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart, Cell, FunnelChart, Funnel, LabelList } from 'recharts';

const COLORS = ['#A38560', '#8B6F47', '#C4A87C', '#725A3A', '#1E4D40', '#16302B'];
const FUNNEL_COLORS = ['#6BCB77', '#A38560', '#C4A87C', '#E8D5B5'];

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [poolCount, setPoolCount] = useState(0);
  const navigate = useNavigate();
  const [ranking, setRanking] = useState([]);
  const [rankFilter, setRankFilter] = useState('');
  const [debts, setDebts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [incomeGoal, setIncomeGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [recentActivity, setRecentActivity] = useState([]);
  const [closingsByMonth, setClosingsByMonth] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState([]);
  const [sectorHeatmap, setSectorHeatmap] = useState([]);
  const [dealsAvg, setDealsAvg] = useState(null);
  const [dealsAvgMonths, setDealsAvgMonths] = useState(12);
  const [brokerActivity, setBrokerActivity] = useState(null);
  const [uncontacted, setUncontacted] = useState([]);
  const [firstContact, setFirstContact] = useState(null);
  const [poolStats, setPoolStats] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const token = sessionStorage.getItem('selecto_token');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      a.download = `SELECTO_CRM_Backup_${now.toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descargado exitosamente');
    } catch { toast.error('Error al generar backup'); }
    setBackingUp(false);
  };

  const loadMetrics = useCallback(() => api.get('/dashboard/metrics').then(setMetrics).catch(() => {}), []);
  const loadRanking = useCallback(() => api.get(`/dashboard/ranking${rankFilter ? `?deal_type=${rankFilter}` : ''}`).then(setRanking).catch(() => {}), [rankFilter]);
  const loadDebts = useCallback(() => api.get('/dashboard/debts').then(setDebts).catch(() => {}), []);
  const loadUpcoming = useCallback(() => api.get('/dashboard/upcoming').then(setUpcoming).catch(() => {}), []);
  const loadMonthlySummary = useCallback(() => api.get('/dashboard/monthly-summary').then(setMonthlySummary).catch(() => {}), []);
  const loadIncomeGoal = useCallback(() => api.get('/users/income-goal').then(setIncomeGoal).catch(() => {}), []);
  const loadRecentActivity = useCallback(() => api.get('/dashboard/recent-activity').then(setRecentActivity).catch(() => {}), []);
  const loadClosingsByMonth = useCallback(() => api.get('/dashboard/closings-by-month').then(setClosingsByMonth).catch(() => {}), []);
  const loadConversionFunnel = useCallback(() => api.get('/dashboard/conversion-funnel').then(setConversionFunnel).catch(() => {}), []);
  const loadSectorHeatmap = useCallback(() => api.get('/dashboard/sector-heatmap').then(setSectorHeatmap).catch(() => {}), []);
  const loadDealsAvg = useCallback(() => api.get(`/dashboard/deals-avg?months=${dealsAvgMonths}`).then(setDealsAvg).catch(() => {}), [dealsAvgMonths]);
  const loadBrokerActivity = useCallback(() => {
    if (user?.role !== 'admin') return;
    api.get('/dashboard/broker-activity').then(setBrokerActivity).catch(() => {});
  }, [user]);
  const loadUncontacted = useCallback(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/uncontacted-leads').then(setUncontacted).catch(() => {});
  }, [user]);
  const loadFirstContact = useCallback(() => {
    if (user?.role !== 'admin') return;
    api.get('/dashboard/first-contact-time').then(setFirstContact).catch(() => {});
  }, [user]);
  const loadPoolStats = useCallback(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/pool-stats').then(setPoolStats).catch(() => {});
  }, [user]);

  const toggleComplete = async (item) => {
    try {
      if (item.type === 'task' && item.task_id) {
        await api.put(`/tasks/${item.task_id}`, { status: item.completed ? 'pendiente' : 'completada' });
      } else if (item.deal_id && item.field) {
        await api.put(`/deals/${item.deal_id}/toggle-complete`, { field: item.field });
      }
      loadUpcoming();
      loadRecentActivity();
    } catch { /* toggle error - UI will retry on next interval */ }
  };

  useEffect(() => { loadMetrics(); loadDebts(); loadUpcoming(); loadMonthlySummary(); loadIncomeGoal(); loadRecentActivity(); loadClosingsByMonth(); loadConversionFunnel(); loadSectorHeatmap(); const loadPool = () => api.get('/crm/leads/pool').then(r => setPoolCount(r.count || 0)).catch(() => {}); loadPool(); const id = setInterval(() => { loadMetrics(); loadDebts(); loadUpcoming(); loadMonthlySummary(); loadRecentActivity(); loadClosingsByMonth(); loadConversionFunnel(); loadSectorHeatmap(); loadPool(); }, 30000); return () => clearInterval(id); }, [loadMetrics, loadDebts, loadUpcoming, loadMonthlySummary, loadIncomeGoal, loadRecentActivity, loadClosingsByMonth, loadConversionFunnel, loadSectorHeatmap]);
  useEffect(() => { loadRanking(); }, [loadRanking]);
  useEffect(() => { loadDealsAvg(); }, [loadDealsAvg]);
  useEffect(() => { loadBrokerActivity(); }, [loadBrokerActivity]);
  useEffect(() => { loadUncontacted(); const id = setInterval(loadUncontacted, 60000); return () => clearInterval(id); }, [loadUncontacted]);
  useEffect(() => { loadFirstContact(); const id = setInterval(loadFirstContact, 300000); return () => clearInterval(id); }, [loadFirstContact]);
  useEffect(() => { loadPoolStats(); const id = setInterval(loadPoolStats, 120000); return () => clearInterval(id); }, [loadPoolStats]);

  const goal = metrics?.goal || 80000000000;
  const accumulated = metrics ? (metrics.total_sales || 0) + (metrics.total_rentals || 0) : 0;
  const progress = metrics ? Math.min((accumulated / goal) * 100, 100) : 0;

  const saveIncomeGoal = async () => {
    const val = typeof goalInput === 'number' ? goalInput : parseFloat(String(goalInput).replace(/,/g, ''));
    if (!val || val <= 0) return;
    try {
      await api.put('/users/income-goal', { income_goal: val });
      setEditingGoal(false);
      loadIncomeGoal();
    } catch { /* save failed */ }
  };

  const metricCards = [
    { label: 'Ventas Totales', value: formatCOP(metrics?.total_sales), icon: TrendingUp, color: '#A38560' },
    { label: 'Arriendos', value: formatCOP(metrics?.total_rentals), icon: Home, color: '#6BCB77' },
    { label: 'Comisiones Generadas', value: formatCOP(metrics?.total_commissions), icon: DollarSign, color: '#A38560' },
    { label: 'Ticket Promedio', split: true, icon: BarChart3, color: '#6BCB77',
      valueSales: formatCOP(metrics?.avg_ticket_sales), valueRentals: formatCOP(metrics?.avg_ticket_rentals),
      pctSales: metrics?.avg_commission_pct_sales, pctRentals: metrics?.avg_commission_pct_rentals },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {poolCount > 0 && (
        <button onClick={() => navigate('/crm')} data-testid="pool-banner"
          className="w-full bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent border border-amber-500/40 rounded-2xl px-5 py-3.5 flex items-center justify-between gap-3 hover:from-amber-500/25 transition-all group">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
              <span className="text-lg">🔥</span>
            </div>
            <div className="min-w-0 text-left">
              <p className="font-outfit text-sm font-semibold text-amber-100">
                {poolCount} lead{poolCount !== 1 ? 's' : ''} disponible{poolCount !== 1 ? 's' : ''} para reclamar
              </p>
              <p className="text-[11px] text-amber-200/70">El primero que lo reclame se queda con él. Click aquí para ir al CRM.</p>
            </div>
          </div>
          <span className="text-xs text-amber-300 group-hover:translate-x-1 transition-transform">Ver CRM →</span>
        </button>
      )}
      {user?.role === 'admin' && uncontacted.length > 0 && (
        <div data-testid="uncontacted-alerts-panel" className="rounded-2xl border border-white/5 bg-[#0A1F1A] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle size={16} strokeWidth={1.5} className="text-amber-400 shrink-0" />
              <div className="min-w-0">
                <p className="font-outfit text-sm font-semibold text-white">Leads sin contactar</p>
                <p className="text-[11px] text-white/40">Nuevos leads asignados que aún no tienen actividad ni nota</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              {[
                { c: 'yellow', label: '24h', cls: 'bg-yellow-400/15 text-yellow-300 border-yellow-400/40' },
                { c: 'orange', label: '48h', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/40' },
                { c: 'red', label: '72h', cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
              ].map(t => {
                const n = uncontacted.filter(u => u.color === t.c).length;
                return (
                  <span key={t.c} data-testid={`uncontacted-count-${t.c}`}
                    className={`px-2 py-1 rounded-full border font-medium ${t.cls} ${n === 0 ? 'opacity-40' : ''}`}>
                    {t.label}: {n}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
            {uncontacted.slice(0, 30).map(u => {
              const cls = u.color === 'red'
                ? 'border-l-red-500 bg-red-500/[0.03]'
                : u.color === 'orange'
                ? 'border-l-orange-500 bg-orange-500/[0.03]'
                : 'border-l-yellow-400 bg-yellow-400/[0.03]';
              const dot = u.color === 'red'
                ? 'bg-red-500' : u.color === 'orange' ? 'bg-orange-500' : 'bg-yellow-400';
              return (
                <button key={u.lead_id} onClick={() => navigate(`/crm/${u.lead_id}`)}
                  data-testid={`uncontacted-row-${u.lead_id}`}
                  className={`w-full px-5 py-3 border-l-2 ${cls} hover:bg-white/[0.03] transition-colors flex items-center gap-3 text-left`}>
                  <span className={`w-2 h-2 rounded-full ${dot} shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white font-medium truncate">{u.lead_name}</p>
                      {u.is_pool_claim && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25 font-medium">POOL</span>
                      )}
                    </div>
                    <p className="text-[11px] text-white/50 truncate">
                      Broker: <span className="text-white/80">{u.broker_name || '(sin asignar)'}</span>
                      {u.source && <span className="text-white/30"> · {u.source}</span>}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold tabular-nums ${
                      u.color === 'red' ? 'text-red-300' : u.color === 'orange' ? 'text-orange-300' : 'text-yellow-300'
                    }`}>{u.hours_since}h</p>
                    <p className="text-[10px] text-white/40">sin contacto</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Dashboard</h1>
          <p className="text-sm text-white/50 mt-1">Bienvenido, {user?.name}</p>
        </div>
        {user?.role === 'admin' && (
          <button onClick={downloadBackup} disabled={backingUp} data-testid="backup-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0A1F1A] border border-white/10 text-white/60 hover:text-white hover:border-[#A38560]/30 transition-colors text-sm disabled:opacity-50">
            <Download size={16} className={backingUp ? 'animate-bounce' : ''} />
            {backingUp ? 'Generando...' : 'Descargar Backup'}
          </button>
        )}
      </div>

      {/* Sales Goal Card */}
      <div data-testid="sales-goal-card" className="relative bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-2/5 h-48 md:h-auto">
            <img src="https://customer-assets.emergentagent.com/job_deal-manager-pro-1/artifacts/432rn78i_IMG_6415.jpeg"
              alt="Meta de ventas" className="w-full h-full object-cover" />
          </div>
          <div className="p-6 md:p-8 flex-1 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Meta de Ventas Anual</p>
            <p className="font-outfit text-4xl font-bold text-white tracking-tight tabular-nums">{formatCOP(goal)}</p>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Ventas acumuladas</span>
                <span className="font-medium text-white tabular-nums">{formatCOP(accumulated)}</span>
              </div>
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-gradient-to-r from-[#A38560] to-[#8B6F47] transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-white/40 text-right tabular-nums">{progress.toFixed(1)}% alcanzado</p>
            </div>
          </div>
        </div>
      </div>

      {/* Broker Income Goal Card */}
      {incomeGoal && (
        <div data-testid="income-goal-card" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5 lg:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-outfit text-base lg:text-lg font-medium text-white flex items-center gap-2">
              <Target size={18} strokeWidth={1.5} className="text-[#6BCB77]" />
              Mi Meta de Ingresos Promedio Mensual
            </h2>
            {!editingGoal && (
              <button onClick={() => { setGoalInput(incomeGoal.income_goal || ''); setEditingGoal(true); }}
                data-testid="edit-income-goal-btn" className="text-white/40 hover:text-[#A38560] transition-colors">
                <Pencil size={16} />
              </button>
            )}
          </div>
          {editingGoal ? (
            <div className="flex items-center gap-3">
              <MoneyInput value={goalInput} onChange={v => setGoalInput(v)} placeholder="Ej: 40,000,000"
                className="bg-[#16302B] border-[#1A3B32] text-white h-11 rounded-xl max-w-xs focus:border-[#A38560]" data-testid="income-goal-input" />
              <button onClick={saveIncomeGoal} className="p-2 bg-[#6BCB77]/20 text-[#6BCB77] rounded-lg hover:bg-[#6BCB77]/30 transition-colors"><Check size={18} /></button>
              <button onClick={() => setEditingGoal(false)} className="p-2 bg-red-400/10 text-red-400 rounded-lg hover:bg-red-400/20 transition-colors"><X size={18} /></button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-3 mb-3">
                <p className="font-outfit text-3xl font-bold text-white tabular-nums">
                  {incomeGoal.income_goal > 0 ? formatCOP(incomeGoal.income_goal) : 'Sin meta definida'}
                </p>
                {incomeGoal.income_goal > 0 && (
                  <span className="text-sm text-white/40">/ mes</span>
                )}
              </div>
              {incomeGoal.income_goal > 0 && (
                <div className="space-y-3">
                  {/* Progress 1: Comisiones generadas / 12 */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Promedio mensual (comisiones generadas / 12)</span>
                      <span className="font-medium text-[#A38560] tabular-nums">{formatCOP(incomeGoal.monthly_avg)}</span>
                    </div>
                    <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#A38560] to-[#8B6F47] transition-all duration-1000 ease-out"
                        style={{ width: `${Math.min((incomeGoal.monthly_avg / incomeGoal.income_goal) * 100, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-white/40">
                      <span>Comisiones generadas del año: {formatCOP(incomeGoal.total_commission_year)}</span>
                      <span className="tabular-nums">{((incomeGoal.monthly_avg / incomeGoal.income_goal) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                  {/* Progress 2: Comisiones realmente recibidas / 12 */}
                  <div className="space-y-1.5 pt-2 border-t border-white/5">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Promedio mensual (comisiones recibidas / 12)</span>
                      <span className="font-medium text-[#6BCB77] tabular-nums">{formatCOP(incomeGoal.received_monthly_avg)}</span>
                    </div>
                    <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#6BCB77] to-[#4EAAAA] transition-all duration-1000 ease-out"
                        style={{ width: `${Math.min((incomeGoal.received_monthly_avg / incomeGoal.income_goal) * 100, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-white/40">
                      <span>Comisiones recibidas del año: {formatCOP(incomeGoal.received_this_year)}</span>
                      <span className="tabular-nums">{((incomeGoal.received_monthly_avg / incomeGoal.income_goal) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              )}
              {!incomeGoal.income_goal && (
                <p className="text-sm text-white/40">Haz clic en el lapiz para definir tu meta de ingresos mensuales promedio.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Recent Activity, Debts & Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Last 8 Days Activity */}
        <div data-testid="recent-activity-block" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
          <h2 className="font-outfit text-base font-medium text-white mb-3 flex items-center gap-2">
            <Eye size={16} strokeWidth={1.5} className="text-[#6BCB77]" /> Ultimos 8 dias
          </h2>
          {recentActivity.length > 0 ? (
            <div className="space-y-2 max-h-56 overflow-auto">
              {recentActivity.map((item, i) => (
                <div key={`${item.type}-${item.date}-${item.deal_id || i}`} className={`flex items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border ${item.overdue ? 'border-red-500/50 bg-red-950/20' : 'border-white/5'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {(item.type === 'step' || item.type === 'payment') ? (
                      <button onClick={() => toggleComplete(item)} data-testid={`toggle-recent-${i}`} className="shrink-0">
                        {item.completed ? <CheckCircle2 size={16} className="text-[#6BCB77]" /> : <Circle size={16} className={item.overdue ? 'text-red-400' : 'text-white/30'} />}
                      </button>
                    ) : item.type === 'visita' ? <Eye size={14} className="text-white/60 shrink-0" /> :
                     item.type === 'oferta' ? <FileText size={14} className="text-[#A38560] shrink-0" /> :
                     <DollarSign size={14} className="text-[#6BCB77] shrink-0" />}
                    <div className="min-w-0">
                      <p className={`font-medium text-sm truncate ${item.completed ? 'line-through text-white/40' : item.overdue ? 'text-red-400' : 'text-white'}`}>{item.title}</p>
                      <p className="text-xs text-white/40 truncate">{item.property && `${item.property}`}{item.client && ` — ${item.client}`}</p>
                    </div>
                  </div>
                  <Badge className={`shrink-0 ml-2 border-0 text-xs ${item.overdue ? 'bg-red-500/10 text-red-400' : item.type === 'closing' ? 'bg-[#6BCB77]/10 text-[#6BCB77]' : item.type === 'oferta' ? 'bg-[#A38560]/10 text-[#A38560]' : 'bg-white/5 text-white/60'}`}>
                    {formatDate(item.date)}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/40 py-2">Sin actividad en los ultimos 8 dias.</p>
          )}
        </div>

        {/* Pending Disbursement Debts */}
        <div data-testid="debts-block" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
          <h2 className="font-outfit text-base font-medium text-white mb-3 flex items-center gap-2">
            <AlertTriangle size={16} strokeWidth={1.5} className="text-[#A38560]" /> Deudas de Dispersion
          </h2>
          {debts.length > 0 ? (
            <div className="space-y-2 max-h-56 overflow-auto">
              {debts.map((d, i) => (
                <div key={`${d.direction}-${d.entity}-${d.deal_id || i}`} className="flex items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border border-white/5">
                  <div className="flex items-center gap-2">
                    {d.direction === 'me_deben' ? (
                      <ArrowDownLeft size={14} className="text-[#6BCB77]" />
                    ) : (
                      <ArrowUpRight size={14} className="text-red-400" />
                    )}
                    <div>
                      <span className={`font-medium ${d.direction === 'me_deben' ? 'text-[#6BCB77]' : 'text-red-400'}`}>
                        {d.direction === 'me_deben' ? `${d.person} te debe` : `Le debes a ${d.person}`}
                      </span>
                      <p className="text-xs text-white/40">{d.property}</p>
                    </div>
                  </div>
                  <span className="font-outfit font-bold tabular-nums text-white">{formatCOP(d.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/40 py-2">No hay deudas pendientes de dispersion.</p>
          )}
        </div>

        {/* Upcoming 8 Days */}
        <div data-testid="upcoming-block" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
          <h2 className="font-outfit text-base font-medium text-white mb-3 flex items-center gap-2">
            <CalendarDays size={16} strokeWidth={1.5} className="text-[#A38560]" /> Proximos 8 dias
          </h2>
          {upcoming.length > 0 ? (
            <div className="space-y-2 max-h-56 overflow-auto">
              {upcoming.map((item, i) => (
                <div key={`${item.type}-${item.date}-${item.deal_id || item.task_id || i}`} className={`flex items-center justify-between p-3 bg-[#16302B] rounded-xl text-sm border ${item.overdue ? 'border-red-500/50 bg-red-950/20' : 'border-white/5'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <button onClick={() => toggleComplete(item)} data-testid={`toggle-upcoming-${i}`} className="shrink-0">
                      {item.completed ? <CheckCircle2 size={16} className="text-[#6BCB77]" /> : <Circle size={16} className={item.overdue ? 'text-red-400' : 'text-white/30'} />}
                    </button>
                    <div className="min-w-0">
                      <p className={`font-medium text-sm truncate ${item.completed ? 'line-through text-white/40' : item.overdue ? 'text-red-400' : 'text-white'}`}>{item.title}</p>
                      <p className="text-xs text-white/40 truncate">
                        {item.property && `${item.property} — `}{item.client}{item.assigned_to_name && `Asignado: ${item.assigned_to_name}`}
                      </p>
                    </div>
                  </div>
                  <Badge className={`shrink-0 ml-2 border-0 text-xs ${item.overdue ? 'bg-red-500/10 text-red-400' : 'bg-[#A38560]/10 text-[#A38560]'}`}>
                    {item.overdue ? 'Vencida' : formatDate(item.date)}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/40 py-2">No hay eventos programados en los proximos 8 dias.</p>
          )}
        </div>
      </div>

      {/* Tiempo hasta primer contacto y Actividad de brokers movidos al final del dashboard */}

      {/* Deals Average per Month */}
      {dealsAvg && (
        <div data-testid="deals-avg-section" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2">
              <BarChart3 size={18} strokeWidth={1.5} className="text-[#A38560]" />
              Promedio de Negocios por Mes
            </h2>
            <div className="flex items-center gap-1 bg-[#16302B] rounded-lg p-1 w-fit">
              {[3, 6, 12, 24].map(n => (
                <button key={n} onClick={() => setDealsAvgMonths(n)} data-testid={`deals-avg-months-${n}`}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${dealsAvgMonths === n ? 'bg-[#A38560] text-[#03110D] font-semibold' : 'text-white/50 hover:text-white'}`}>
                  {n}m
                </button>
              ))}
            </div>
          </div>
          {/* Global averages */}
          <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-4">
            <div className="bg-[#16302B] rounded-xl p-4 border border-white/5" data-testid="deals-avg-overall">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Negocios / mes</p>
              <p className="font-outfit text-3xl font-medium text-white tabular-nums">{dealsAvg.monthly_avg?.toFixed(2) || '0'}</p>
              <p className="text-[11px] text-white/40 mt-1">{dealsAvg.total_deals} en {dealsAvg.months} meses</p>
            </div>
            <div className="bg-[#16302B] rounded-xl p-4 border border-emerald-500/20" data-testid="deals-avg-sales">
              <p className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1.5">Ventas / mes</p>
              <p className="font-outfit text-3xl font-medium text-emerald-400 tabular-nums">{dealsAvg.sales_avg?.toFixed(2) || '0'}</p>
              <p className="text-[11px] text-white/40 mt-1">{dealsAvg.total_sales} cierres</p>
            </div>
            <div className="bg-[#16302B] rounded-xl p-4 border border-[#A38560]/20" data-testid="deals-avg-rentals">
              <p className="text-[10px] uppercase tracking-widest text-[#A38560] mb-1.5">Arriendos / mes</p>
              <p className="font-outfit text-3xl font-medium text-[#A38560] tabular-nums">{dealsAvg.rentals_avg?.toFixed(2) || '0'}</p>
              <p className="text-[11px] text-white/40 mt-1">{dealsAvg.total_rentals} cierres</p>
            </div>
          </div>
          {/* Per-broker breakdown */}
          {dealsAvg.by_broker && dealsAvg.by_broker.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Promedio mensual por broker</p>
              <div className="overflow-x-auto -mx-4 lg:mx-0">
                <table className="w-full text-sm" data-testid="deals-avg-brokers">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                      <th className="text-left py-2 px-4">Broker</th>
                      <th className="text-right py-2 px-2">Total / mes</th>
                      <th className="text-right py-2 px-2 text-emerald-400/70">Ventas / mes</th>
                      <th className="text-right py-2 px-2 text-[#A38560]/70">Arriendos / mes</th>
                      <th className="text-right py-2 px-4 text-white/30">En {dealsAvg.months}m</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealsAvg.by_broker.map(b => (
                      <tr key={b.broker_id} data-testid={`broker-avg-${b.broker_id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-2.5 px-4 text-white font-medium">{b.name}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-white">{b.monthly_avg.toFixed(2)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-emerald-400">{b.sales_avg.toFixed(2)}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-[#A38560]">{b.rentals_avg.toFixed(2)}</td>
                        <td className="py-2.5 px-4 text-right tabular-nums text-white/40">{b.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Monthly Summary Section */}
      {monthlySummary && (
        <div data-testid="monthly-summary" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
          <h2 className="font-outfit text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Target size={18} strokeWidth={1.5} className="text-[#A38560]" />
            Resumen del Mes ({monthlySummary.month}/{monthlySummary.year})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
            <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
              <Eye size={24} strokeWidth={1.5} className="text-white/40 mx-auto mb-2" />
              <p className="font-outfit text-2xl font-bold text-white">{monthlySummary.visits}</p>
              <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Visitas</p>
              <p data-testid="my-visits" className="font-outfit text-sm font-medium text-white/50 mt-1.5">Tu: {monthlySummary.my_visits}</p>
            </div>
            <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
              <FileText size={24} strokeWidth={1.5} className="text-[#A38560] mx-auto mb-2" />
              <p className="font-outfit text-2xl font-bold text-[#A38560]">{monthlySummary.offers}</p>
              <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Ofertas</p>
              <p data-testid="my-offers" className="font-outfit text-sm font-medium text-[#A38560]/60 mt-1.5">Tu: {monthlySummary.my_offers}</p>
            </div>
            <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
              <DollarSign size={24} strokeWidth={1.5} className="text-[#6BCB77] mx-auto mb-2" />
              <p className="font-outfit text-2xl font-bold text-[#6BCB77]">{monthlySummary.deals}</p>
              <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Negocios Cerrados</p>
              <p data-testid="my-deals" className="font-outfit text-sm font-medium text-[#6BCB77]/60 mt-1.5">Tu: {monthlySummary.my_deals}</p>
            </div>
            <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
              <Building2 size={24} strokeWidth={1.5} className="text-white/60 mx-auto mb-2" />
              <p className="font-outfit text-2xl font-bold text-white">{monthlySummary.captures}</p>
              <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Captaciones</p>
              <p data-testid="my-captures" className="font-outfit text-sm font-medium text-white/50 mt-1.5">Tu: {monthlySummary.my_captures}</p>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="metrics-grid">
        {metricCards.map((card) => (
          <div key={card.label} data-testid={`metric-card-${card.label}`}
            className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5 hover:border-[#A38560]/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-white/40">{card.label}</p>
              <card.icon size={18} strokeWidth={1.5} style={{ color: card.color }} />
            </div>
            {card.split ? (
              <div className="space-y-3 border-t border-white/5 pt-3">
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-white/30 uppercase">Venta</p>
                    <p className="font-outfit text-lg font-bold text-white tracking-tight tabular-nums">{card.valueSales}</p>
                  </div>
                  {card.pctSales != null && (
                    <p className="text-[10px] text-white/40 mt-0.5 flex items-center justify-between" data-testid="avg-pct-sales">
                      <span>% Mediación promedio</span>
                      <span className="text-[#A38560] font-semibold tabular-nums">{Number(card.pctSales || 0).toFixed(2)}%</span>
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-white/30 uppercase">Arriendo</p>
                    <p className="font-outfit text-lg font-bold text-white tracking-tight tabular-nums">{card.valueRentals}</p>
                  </div>
                  {card.pctRentals != null && (
                    <p className="text-[10px] text-white/40 mt-0.5 flex items-center justify-between" data-testid="avg-pct-rentals">
                      <span>% Mediación promedio</span>
                      <span className="text-[#A38560] font-semibold tabular-nums">{Number(card.pctRentals || 0).toFixed(2)}%</span>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="font-outfit text-2xl font-bold text-white tracking-tight tabular-nums">{card.value}</p>
            )}
          </div>
        ))}
      </div>

      {/* Closings by Month Chart */}
      {closingsByMonth.length > 0 && (
        <div data-testid="closings-by-month" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
          <h2 className="font-outfit text-lg font-medium text-white mb-4 flex items-center gap-2">
            <BarChart3 size={18} strokeWidth={1.5} className="text-[#A38560]" />
            Cierres por Mes (Equipo)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={closingsByMonth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} tickLine={false} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
              <YAxis yAxisId="count" orientation="left" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis yAxisId="value" orientation="right" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1e9 ? `${(v/1e9).toFixed(0)}B` : v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                formatter={(value, name) => [name === 'Valor' ? formatCOP(value) : value, name]}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
              />
              <Bar yAxisId="count" dataKey="count" name="Negocios" fill="#A38560" radius={[6, 6, 0, 0]} barSize={32} />
              <Line yAxisId="value" type="monotone" dataKey="value" name="Valor" stroke="#6BCB77" strokeWidth={2} dot={{ r: 4, fill: '#6BCB77' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Conversion Funnel & Sector Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Conversion Funnel by Broker */}
        {conversionFunnel.length > 0 && (
          <div data-testid="conversion-funnel" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
            <h2 className="font-outfit text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Filter size={18} strokeWidth={1.5} className="text-[#6BCB77]" />
              Embudo de Conversion ({new Date().getFullYear()})
            </h2>
            <div className="space-y-3 overflow-auto max-h-[400px]">
              <div className="grid grid-cols-6 gap-2 text-xs text-white/40 uppercase tracking-wider font-medium px-1 sticky top-0 bg-[#0A1F1A] pb-2">
                <span className="col-span-2">Broker</span>
                <span className="text-center">Visitas</span>
                <span className="text-center">Ofertas</span>
                <span className="text-center">Cierres</span>
                <span className="text-center">V-C %</span>
              </div>
              {conversionFunnel.filter(b => b.visits > 0 || b.offers > 0 || b.closings > 0).map(b => (
                <div key={b.id} className="grid grid-cols-6 gap-2 items-center bg-[#16302B] rounded-xl p-3 border border-white/5">
                  <span className="col-span-2 text-sm text-white font-medium truncate">{b.name}</span>
                  <span className="text-center text-sm text-white/70 tabular-nums">{b.visits}</span>
                  <div className="text-center">
                    <span className="text-sm text-[#A38560] tabular-nums">{b.offers}</span>
                    {b.v2o > 0 && <p className="text-[10px] text-white/30">{b.v2o}%</p>}
                  </div>
                  <div className="text-center">
                    <span className="text-sm text-[#6BCB77] font-medium tabular-nums">{b.closings}</span>
                    {b.o2c > 0 && <p className="text-[10px] text-white/30">{b.o2c}%</p>}
                  </div>
                  <div className="text-center">
                    <span className={`text-sm font-bold tabular-nums ${b.v2c >= 10 ? 'text-[#6BCB77]' : b.v2c > 0 ? 'text-[#A38560]' : 'text-white/30'}`}>{b.v2c}%</span>
                  </div>
                </div>
              ))}
              {conversionFunnel.filter(b => b.visits > 0 || b.offers > 0 || b.closings > 0).length === 0 && (
                <p className="text-sm text-white/40 py-4 text-center">Sin actividad registrada este ano.</p>
              )}
            </div>
          </div>
        )}

        {/* Sector Heatmap */}
        {sectorHeatmap.length > 0 && (
          <div data-testid="sector-heatmap" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
            <h2 className="font-outfit text-lg font-medium text-white mb-4 flex items-center gap-2">
              <MapPin size={18} strokeWidth={1.5} className="text-[#A38560]" />
              Mapa de Calor por Sector
            </h2>
            <div className="space-y-2 overflow-auto max-h-[400px]">
              {sectorHeatmap.map((s, i) => {
                const maxVal = sectorHeatmap[0]?.value || 1;
                const pct = (s.value / maxVal) * 100;
                const intensity = Math.max(0.15, pct / 100);
                return (
                  <div key={s.sector} className="relative overflow-hidden rounded-xl border border-white/5">
                    <div className="absolute inset-0 rounded-xl" style={{ backgroundColor: `rgba(163,133,96,${intensity * 0.3})` }} />
                    <div className="relative flex items-center justify-between p-3 lg:p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{s.sector}</p>
                        <p className="text-xs text-white/40">{s.ventas} venta{s.ventas !== 1 ? 's' : ''} · {s.arriendos} arriendo{s.arriendos !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="font-outfit text-lg font-bold text-white tabular-nums">{s.count}</p>
                        <p className="text-xs text-[#A38560] tabular-nums">{formatCOP(s.value)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Broker Ranking */}
      <div data-testid="ranking-chart" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="font-outfit text-lg font-medium text-white">Ranking de Brokers</h2>
          <div className="flex gap-1 bg-[#16302B] rounded-xl p-1">
            {[{v:'',l:'Todos'},{v:'venta',l:'Ventas'},{v:'arriendo',l:'Arriendos'}].map(f => (
              <button key={f.v} data-testid={`rank-filter-${f.v || 'all'}`} onClick={() => setRankFilter(f.v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${rankFilter === f.v ? 'bg-[#A38560] text-[#03110D]' : 'text-white/50 hover:text-white'}`}>
                {f.l}
              </button>
            ))}
          </div>
        </div>
        {ranking.filter(r => r.closing_total > 0).length > 0 ? (
          <div className="space-y-3">
            {ranking.filter(r => r.closing_total > 0).sort((a, b) => b.closing_total - a.closing_total).map((broker, i) => {
              const maxVal = Math.max(...ranking.map(r => r.closing_total));
              const pct = maxVal > 0 ? (broker.closing_total / maxVal) * 100 : 0;
              return (
                <div key={broker.id || broker.name} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-sm text-white/60 sm:w-32 truncate">{broker.name}</span>
                  <div className="flex-1 relative">
                    <div className="h-8 rounded-lg bg-[#16302B] overflow-hidden">
                      <div className="h-full rounded-lg flex items-center px-2 sm:px-3 transition-all duration-700"
                        style={{ width: `${Math.max(pct, 20)}%`, backgroundColor: COLORS[i % COLORS.length] }}>
                        <span className="text-[10px] sm:text-xs font-bold text-[#03110D] tabular-nums truncate">{formatCOP(broker.closing_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-white/40">
            <p>Aun no hay datos de produccion. Crea negocios para ver el ranking.</p>
          </div>
        )}
      </div>

      {user?.role === 'admin' && poolStats && (
        <div data-testid="pool-stats-panel" className="rounded-2xl border border-white/5 bg-[#0A1F1A] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
              <Users size={14} className="text-amber-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-outfit text-sm font-semibold text-white">Pool de Leads</p>
              <p className="text-[11px] text-white/40">Leads que han llegado al pool y cuántos ha reclamado cada broker</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Total al pool</p>
                <p className="font-outfit text-xl font-semibold text-amber-300 tabular-nums" data-testid="pool-total">
                  {poolStats.total_pool_leads}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Activos sin reclamar</p>
                <p className="font-outfit text-xl font-semibold text-white/80 tabular-nums" data-testid="pool-active">
                  {poolStats.active_in_pool}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Reclamados</p>
                <p className="font-outfit text-xl font-semibold text-emerald-300 tabular-nums" data-testid="pool-claimed">
                  {poolStats.total_claimed}
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
            {poolStats.brokers.length === 0 && (
              <p className="px-5 py-6 text-white/40 text-sm text-center">Aún no hay brokers activos.</p>
            )}
            {poolStats.brokers.map(b => {
              const pct = poolStats.total_claimed > 0 ? Math.round((b.count / poolStats.total_claimed) * 100) : 0;
              return (
                <div key={b.broker_id} data-testid={`pool-broker-${b.broker_id}`}
                  className="px-5 py-3 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${b.count > 0 ? 'bg-amber-400' : 'bg-white/20'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${b.count > 0 ? 'text-white' : 'text-white/60'}`}>{b.broker_name || '(sin nombre)'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-1 flex-1 max-w-[240px] rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-amber-400/70" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] text-white/40 tabular-nums">{pct}%</span>
                    </div>
                  </div>
                  <p className={`text-right font-outfit text-lg font-semibold tabular-nums shrink-0 ${b.count > 0 ? 'text-amber-300' : 'text-white/30'}`}>{b.count}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tiempo hasta primer contacto (Admin only) — al final del dashboard */}
      {user?.role === 'admin' && firstContact && firstContact.brokers?.length > 0 && (
        <div data-testid="first-contact-panel" className="rounded-2xl border border-white/5 bg-[#0A1F1A] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <CalendarDays size={16} strokeWidth={1.5} className="text-[#6BCB77] shrink-0" />
              <div className="min-w-0">
                <p className="font-outfit text-sm font-semibold text-white">Tiempo hasta primer contacto</p>
                <p className="text-[11px] text-white/40">Cuánto tarda el equipo en dar la primera actividad tras crear un lead</p>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Promedio equipo</p>
                <p className="font-outfit text-2xl font-semibold text-[#A38560] tabular-nums" data-testid="team-avg-first-contact">
                  {(() => {
                    const h = firstContact.team?.avg_hours;
                    if (h == null) return '—';
                    return h >= 24 ? `${(h/24).toFixed(1)} d` : `${h.toFixed(1)} h`;
                  })()}
                </p>
                <p className="text-[10px] text-white/40">sobre {firstContact.team?.count || 0} lead{(firstContact.team?.count || 0) !== 1 ? 's' : ''} contactado{(firstContact.team?.count || 0) !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
            {firstContact.brokers.map(b => {
              const teamAvg = firstContact.team?.avg_hours || 0;
              const noData = b.count === 0 || b.avg_hours == null;
              const better = !noData && teamAvg > 0 && b.avg_hours <= teamAvg;
              const label = noData ? '—' : (b.avg_hours >= 24 ? `${(b.avg_hours/24).toFixed(1)} d` : `${b.avg_hours.toFixed(1)} h`);
              const dotCls = noData ? 'bg-white/20' : (better ? 'bg-emerald-400' : 'bg-orange-400');
              const valueCls = noData ? 'text-white/30' : (better ? 'text-emerald-300' : 'text-orange-300');
              return (
                <div key={b.broker_id} data-testid={`first-contact-row-${b.broker_id}`}
                  className="px-5 py-3 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${dotCls}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${noData ? 'text-white/60' : 'text-white'}`}>{b.broker_name || '(sin nombre)'}</p>
                    <p className="text-[11px] text-white/40">
                      {noData ? 'Sin leads contactados aún' : (
                        <>
                          {b.count} lead{b.count !== 1 ? 's' : ''} contactado{b.count !== 1 ? 's' : ''}
                          {b.median_hours != null && <span className="text-white/30"> · mediana {b.median_hours >= 24 ? `${(b.median_hours/24).toFixed(1)} d` : `${b.median_hours.toFixed(1)} h`}</span>}
                        </>
                      )}
                    </p>
                  </div>
                  <p className={`text-right font-outfit text-lg font-semibold tabular-nums shrink-0 ${valueCls}`}>{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Broker Activity (Admin only) — al final del dashboard */}
      {brokerActivity && user?.role === 'admin' && (
        <div data-testid="broker-activity-section" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
          <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2 mb-4">
            <Users size={18} strokeWidth={1.5} className="text-[#A38560]" />
            Actividad de brokers en el CRM
          </h2>
          <p className="text-xs text-white/40 -mt-2 mb-4">Sesiones diarias, tiempo dentro del CRM y última vez que revisó — compara mes actual vs mes anterior.</p>
          <div className="overflow-x-auto -mx-4 lg:mx-0">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                  <th className="text-left py-2 px-4">Broker</th>
                  <th className="text-right py-2 px-2">Hoy</th>
                  <th className="text-right py-2 px-2">Mes actual<br /><span className="text-white/20 normal-case">Sesiones · Min</span></th>
                  <th className="text-right py-2 px-2">Mes anterior<br /><span className="text-white/20 normal-case">Sesiones · Min</span></th>
                  <th className="text-right py-2 px-4">Última visita</th>
                </tr>
              </thead>
              <tbody>
                {brokerActivity.brokers.map(b => {
                  const trend = b.sessions_this_month - b.sessions_last_month;
                  const last = b.last_active_at ? new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(b.last_active_at)) : '—';
                  return (
                    <tr key={b.id} data-testid={`broker-activity-${b.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2.5 px-4">
                        <div className="text-white font-medium">{b.name}</div>
                        <div className="text-[10px] text-white/30 capitalize">{b.role}</div>
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <div className="tabular-nums text-white">{b.sessions_today}</div>
                        <div className="text-[10px] text-white/40 tabular-nums">{b.minutes_today}m</div>
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <div className="flex items-baseline justify-end gap-1.5">
                          <span className="tabular-nums text-white">{b.sessions_this_month}</span>
                          <span className={`text-[9px] tabular-nums ${trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-red-400' : 'text-white/30'}`}>
                            {trend > 0 ? '+' : ''}{trend}
                          </span>
                        </div>
                        <div className="text-[10px] text-white/40 tabular-nums">{b.minutes_this_month}m</div>
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <div className="tabular-nums text-white/60">{b.sessions_last_month}</div>
                        <div className="text-[10px] text-white/30 tabular-nums">{b.minutes_last_month}m</div>
                      </td>
                      <td className="py-2.5 px-4 text-right text-white/60 text-xs tabular-nums">{last}</td>
                    </tr>
                  );
                })}
                {brokerActivity.brokers.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-white/30">Sin brokers activos</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {user?.role === 'admin' && (
        <>
          <CommissionSummary target="agency" title="Comisiones Inmobiliaria SELECTO"
            subtitle="Parte de la Agencia en cada negocio. Pagado = dispersiones registradas hacia 'Agencia SELECTO'." />
          <CommissionSummary target="deeplook" title="Comisiones Deeplook"
            subtitle="Negocios vinculados a Deeplook. Pagado = dispersiones registradas hacia 'Deeplook'." />
          <BrokerCommissionChart />
          <FollowUpRanking />
        </>
      )}
      <LostReasonsSummary isAdmin={user?.role === 'admin'} />
    </div>
  );
}
